import { ComponentChildren } from "preact"
import { useMemo } from "preact/hooks"
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { BASELINE_COLOR, row_tooltip, DataTable, Figure, GRID_COLOR, InlineBar, Legend, Panel, WORKING_COLOR, column } from "./trade_review_parts"
import { fill_row, fmt_day, fmt_pct0, fmt_usd_short, trade_review, watch_row } from "./trade_review_model"

function WatchChart({ title, note, rows, top }: { title: string; note: ComponentChildren; rows: watch_row[]; top: number }) {
  return (
    <div class="space-y-1 min-w-0">
      <h3 class="text-sm font-medium">{title}</h3>
      <p class="text-sm text-muted-foreground">{note}</p>
      <div class="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 6, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={GRID_COLOR} />
            <XAxis dataKey="week" tickFormatter={fmt_day} minTickGap={36} tick={{ fontSize: 12 }} />
            <YAxis domain={[0, top]} tickFormatter={fmt_usd_short} width={44} tick={{ fontSize: 12 }} />
            <Tooltip
              content={row_tooltip((row: watch_row) => [`Week of ${fmt_day(row.week)}`, `Shipped ${fmt_usd_short(row.shipped)}`, `Ordered ${fmt_usd_short(row.ordered)}`]) as never}
            />
            <Bar dataKey="shipped" fill={WORKING_COLOR} isAnimationActive={false} />
            <Line dataKey="ordered" stroke={BASELINE_COLOR} strokeWidth={1.5} dot={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

const FILL_COLUMNS: column<fill_row>[] = [
  { header: "Product family", left: true, cell: (row: fill_row) => row.family },
  { header: "Ordered", cell: (row: fill_row) => fmt_usd_short(row.ordered) },
  { header: "Shipped", cell: (row: fill_row) => fmt_usd_short(row.shipped) },
  { header: "Short", cell: (row: fill_row) => fmt_usd_short(row.short) },
  { header: "Fill rate", left: true, cell: (row: fill_row) => <InlineBar share={row.fill} label={fmt_pct0(row.fill)} color={row.fill < 0.7 ? "#e34948" : undefined} /> },
]

export function WatchTab({ data }: { data: trade_review }) {
  const s = data.summary
  const top = useMemo(() => Math.max(...data.watch_weekly.map((row: watch_row) => Math.max(row.shipped, row.ordered))), [data.watch_weekly])

  function series(name: string): watch_row[] {
    return data.watch_weekly.filter((row: watch_row) => row.series === name)
  }

  return (
    <div class="space-y-6">
      <Panel title="Volume that stopped" note="Dollars KeHE shipped each week, all on one scale. The line shows what stores ordered, so a gap between line and bars is an unfilled order. Kroger moved to GFI. For the other three we can see that they stopped, not why. Your team will know which were planned.">
        <div class="grid gap-6 lg:grid-cols-2">
          <WatchChart title="Kroger (Ralphs, Fry's), all items" top={top} rows={series("Kroger (Ralphs, Fry's), all items")} note={<>Moved, not lost. Was <Figure summary={s} field="kroger_wk" as="usd_short" /> a week through KeHE until the week of June 7. Ralphs now ships through GFI Colton and Fry's through GFI Phoenix, and GFI sales are not in this review. KeHE has billed <Figure summary={s} field="kroger_billed_since" as="usd_short" /> of Kroger charges since.</>} />
          <WatchChart title="Sprouts, Marin French Petites" top={top} rows={series("Sprouts, Marin French Petites")} note={<>Was <Figure summary={s} field="sprouts_petites_wk" as="usd_short" /> a week. Orders outran shipments in June, then both stopped.</>} />
          <WatchChart title="Sprouts, Laura Chenel Marinated" top={top} rows={series("Sprouts, Laura Chenel Marinated")} note={<>Was <Figure summary={s} field="sprouts_marinated_wk" as="usd_short" /> a week, with orders about double shipments all year.</>} />
          <WatchChart title="Safeway Seattle, Laura Chenel goat" top={top} rows={series("Safeway Seattle, Laura Chenel goat")} note={<>Was <Figure summary={s} field="seattle_lc_wk" as="usd_short" /> a week, now <Figure summary={s} field="seattle_lc_after" as="usd_short" />. Marin French kept shipping there, and September scans were still billed.</>} />
        </div>
        <Legend items={[{ label: "Shipped", color: WORKING_COLOR }, { label: "Ordered", color: BASELINE_COLOR }]} />
      </Panel>
      <Panel title="Fill rate by product family" note={<>KeHE's store orders against what shipped, at KeHE's cost, year to date. Overall fill is <Figure summary={s} field="fill_total" as="pct0" />. A shorted order can be placed again the next week, so the short column is a ceiling.</>}>
        <DataTable columns={FILL_COLUMNS} rows={data.fill_by_family} row_key={(row: fill_row) => row.family} />
      </Panel>
    </div>
  )
}
